import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["src", "alt", "className", "style"],
  _excluded2 = ["children", "delay", "className", "style", "xstyle"],
  _excluded3 = ["backgroundColor", "children", "className", "layoutId", "shape", "size", "style", "truncated", "xstyle"],
  _excluded4 = ["children", "className", "shape", "size", "style", "xstyle"],
  _excluded5 = ["children", "className", "size", "style", "xstyle"];
import { c as _c } from "react-compiler-runtime";
import { createContext, forwardRef, useContext, useState } from 'react';
import { Avatar as BaseAvatar } from '@base-ui/react/avatar';
import * as stylex from '@stylexjs/stylex';
import { useLayoutConstraints } from '../layout/LayoutConstraints';
import { DATA_COMPONENT_ATTRIBUTE } from '../../constants/dataAttributes';
import { TextRoot } from '../Text/primitives';
import { TooltipTrigger, createTooltipHandle } from '../Tooltip/primitives';
import { theme } from 'trellis-theming/modern-theme.stylex';
import { jsx as _jsx } from "react/jsx-runtime";
const COMPONENT_NAME = 'Avatar';
const AVATAR_GROUP_NAME = 'AvatarGroup';
const AVATAR_GROUP_COUNT_NAME = 'AvatarGroupCount';
const AVATAR_FALLBACK_NAME = 'AvatarFallback';
const AvatarGroupContext = /*#__PURE__*/createContext(false);
const AvatarGroupSizeContext = /*#__PURE__*/createContext(undefined);
const AvatarTooltipHandleContext = /*#__PURE__*/createContext(null);
const containerBaseStyles = {
  default: {
    borderStyle: "x1y0btm7",
    borderWidth: "xmhl9si",
    display: "x3nfvp2",
    flexShrink: "x2lah0s",
    position: "x1n2onr6",
    userSelect: "x87ps6o",
    verticalAlign: "xxymvpz",
    $$css: true
  }
};
const containerSizeStyles = {
  xs: {
    height: "x15zhq6g",
    width: "x11lf007",
    $$css: true
  },
  sm: {
    height: "x1t6vu2y",
    width: "xdcih3v",
    $$css: true
  },
  md: {
    height: "x1is74mz",
    width: "xmn0uq4",
    $$css: true
  }
};
const containerShapeStyles = {
  circle: {
    borderRadius: "x1sq5fu9",
    borderStartStartRadius: null,
    borderStartEndRadius: null,
    borderEndStartRadius: null,
    borderEndEndRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    $$css: true
  },
  square: {
    borderRadius: "x1nq0khy",
    borderStartStartRadius: null,
    borderStartEndRadius: null,
    borderEndStartRadius: null,
    borderEndEndRadius: null,
    borderTopLeftRadius: null,
    borderTopRightRadius: null,
    borderBottomLeftRadius: null,
    borderBottomRightRadius: null,
    $$css: true
  }
};
const containerVariantStyles = {
  blue: {
    backgroundColor: "x1vvh66u x1ckwljq x1jlk7ng x1g8lvc1",
    borderColor: "xdoioh1 x151wlpg x1o6pckt x1pdt50k",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  green: {
    backgroundColor: "xapn8a x1pbautx x1iqjqly xed4znw",
    borderColor: "x1h7f7h1 xgly90k x1nqxo05 x1x7p6ps",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  lavender: {
    backgroundColor: "x1fv8lh7 xgoatny x1gcbci3 x1tzeypr",
    borderColor: "x17f4jko x19r54fn xwbq3zg x1f7hdq0",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  magenta: {
    backgroundColor: "x1awpt1v x1m0clbt xm8hmzl x1m8xgvz",
    borderColor: "xdc6xm x2tsqmp xu49ejl x12zun4u",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  neutral: {
    backgroundColor: "x1up649o xd9q09 x1dvxcqe xq6qv9p",
    borderColor: "x17ffr1w xtcvngh x1rv6ge1 x1bsnzwo",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  orange: {
    backgroundColor: "x1rtsepq x1pf0zoh x6tqp2d x1eoc3id",
    borderColor: "xwgtzp4 x1besujn xqnhri9 x13b11za",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  purple: {
    backgroundColor: "x1aemck1 x1wfzqnd x1ma9qhm x1jfpvxe",
    borderColor: "xmxsbc7 x1a0g7ab x1ixfwar xfaryb5",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  red: {
    backgroundColor: "xpsndpc x1nzgxfp x1ojt4g5 xbls4ce",
    borderColor: "xmf4m7t x19heb10 xkigrnm xq4ftcs",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  teal: {
    backgroundColor: "x3qziqg x1vxv8ut xwy0c4o xhfb5jn",
    borderColor: "x1aclnqu x16q2g3g x1ggqljh xajvnik",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  },
  yellow: {
    backgroundColor: "x12ed9og x1p3ezao xg481rn xjdikr1",
    borderColor: "xz9331m x19lz7r4 x8a55fh xyxnuh9",
    borderInlineColor: null,
    borderInlineStartColor: null,
    borderLeftColor: null,
    borderInlineEndColor: null,
    borderRightColor: null,
    borderBlockColor: null,
    borderTopColor: null,
    borderBottomColor: null,
    $$css: true
  }
};
const containerGroupStyles = {
  default: {
    boxShadow: "x1mky0zj",
    marginInlineStart: "x1nrye9d xphmvto",
    marginLeft: null,
    marginRight: null,
    $$css: true
  }
};
const labelBaseStyles = {
  default: {
    alignItems: "x6s0dn4",
    height: "x5yr21d",
    borderRadius: "x1pjcqnp",
    color: "xnp8pqq xaztlo0 xumsw33 x1dap7mz",
    display: "x78zum5",
    fontFamily: "xq33457",
    fontSize: "x115jnxo",
    fontWeight: "x9x4hfr",
    width: "xh8yej3",
    justifyContent: "xl56j7k",
    letterSpacing: "x1q73f03",
    lineHeight: "xg0k5v1",
    overflow: "xb3r6kr",
    $$css: true
  }
};
const groupBaseStyles = {
  default: {
    alignItems: "x6s0dn4",
    display: "x78zum5",
    $$css: true
  }
};
const groupCountBaseStyles = {
  default: {
    alignItems: "x6s0dn4",
    backgroundColor: "x1j6g2un",
    boxShadow: "x1mky0zj",
    color: "x69kfgm",
    display: "x3nfvp2",
    flexShrink: "x2lah0s",
    fontSize: "x115jnxo",
    fontWeight: "x9x4hfr",
    justifyContent: "xl56j7k",
    marginInlineStart: "x1nrye9d",
    position: "x1n2onr6",
    userSelect: "x87ps6o",
    verticalAlign: "xxymvpz",
    $$css: true
  }
};
const groupCountSizeStyles = {
  xs: {
    height: "x15zhq6g",
    width: "x11lf007",
    $$css: true
  },
  sm: {
    height: "x1t6vu2y",
    width: "xdcih3v",
    $$css: true
  },
  md: {
    height: "x1is74mz",
    width: "xmn0uq4",
    $$css: true
  }
};

/**
 * Renders the avatar image inside a `Avatar`. Falls back to
 * `AvatarFallback` when the image fails to load.
 */
export const AvatarImage = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(10);
  let alt;
  let restProps;
  let src;
  if ($[0] !== t0) {
    const {
        src: t1,
        alt: t2
      } = t0,
      t3 = _objectWithoutPropertiesLoose(t0, _excluded);
    src = t1;
    alt = t2;
    restProps = t3;
    $[0] = t0;
    $[1] = alt;
    $[2] = restProps;
    $[3] = src;
  } else {
    alt = $[1];
    restProps = $[2];
    src = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      className: "x5yr21d x1pjcqnp xh8yej3 xl1xv1r"
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== alt || $[6] !== ref || $[7] !== restProps || $[8] !== src) {
    t2 = /*#__PURE__*/_jsx(BaseAvatar.Image, Object.assign({}, restProps, {
      ref: ref,
      src: src,
      alt: alt
    }, t1));
    $[5] = alt;
    $[6] = ref;
    $[7] = restProps;
    $[8] = src;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
});
AvatarImage.displayName = 'AvatarImage';

/**
 * Content shown when no image is available or while it loads. Typically
 * displays initials. Accepts an optional `delay` in milliseconds.
 */
export const AvatarFallback = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(17);
  let children;
  let delay;
  let restProps;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        delay: t2,
        xstyle: t3
      } = t0,
      t4 = _objectWithoutPropertiesLoose(t0, _excluded2);
    children = t1;
    delay = t2;
    xstyle = t3;
    restProps = t4;
    $[0] = t0;
    $[1] = children;
    $[2] = delay;
    $[3] = restProps;
    $[4] = xstyle;
  } else {
    children = $[1];
    delay = $[2];
    restProps = $[3];
    xstyle = $[4];
  }
  const tooltipHandle = useContext(AvatarTooltipHandleContext);
  let t1;
  if ($[5] !== xstyle) {
    t1 = stylex.props(labelBaseStyles.default, xstyle);
    $[5] = xstyle;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [DATA_COMPONENT_ATTRIBUTE]: AVATAR_FALLBACK_NAME
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== tooltipHandle) {
    t3 = tooltipHandle ? /*#__PURE__*/_jsx(TextRoot, {
      truncated: true,
      tooltipHandle: tooltipHandle,
      children: children
    }) : children;
    $[8] = children;
    $[9] = tooltipHandle;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== delay || $[12] !== ref || $[13] !== restProps || $[14] !== t1 || $[15] !== t3) {
    t4 = /*#__PURE__*/_jsx(BaseAvatar.Fallback, Object.assign({}, restProps, {
      ref: ref,
      delay: delay
    }, t1, t2, {
      children: t3
    }));
    $[11] = delay;
    $[12] = ref;
    $[13] = restProps;
    $[14] = t1;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
});
AvatarFallback.displayName = AVATAR_FALLBACK_NAME;

// TODO: Restore AvatarBadge when UX formalizes Badge support. See trellis-issues#1659.
// export const AvatarBadge = forwardRef<HTMLSpanElement, AvatarBadgeProps>(
//   (
//     {
//       variant = 'online',
//       'aria-label': ariaLabel,
//       className: __noClassName,
//       style: __noStyle,
//       xstyle,
//       ...restProps
//     },
//     ref
//   ) => {
//     return (
//       <span
//         ref={ref}
//         role="img"
//         aria-label={ariaLabel ?? variant}
//         {...restProps}
//       />
//     );
//   }
// );
// AvatarBadge.displayName = 'AvatarBadge';

/**
 * Isolates the truncation tooltip wiring (handle creation, context, trigger)
 * so non-truncated avatars pay zero hook or tooltip cost.
 */
const TruncatedAvatarShell = t0 => {
  const $ = _c(6);
  const {
    children
  } = t0;
  const [tooltipHandle] = useState(_temp);
  let t1;
  if ($[0] !== children || $[1] !== tooltipHandle) {
    t1 = /*#__PURE__*/_jsx(TooltipTrigger, {
      handle: tooltipHandle,
      render: children
    });
    $[0] = children;
    $[1] = tooltipHandle;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1 || $[4] !== tooltipHandle) {
    t2 = /*#__PURE__*/_jsx(AvatarTooltipHandleContext.Provider, {
      value: tooltipHandle,
      children: t1
    });
    $[3] = t1;
    $[4] = tooltipHandle;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
TruncatedAvatarShell.displayName = 'TruncatedAvatarShell';

/**
 * Displays a visual representation of a user or entity, falling back to initials or a placeholder when no image is available. Used in profile displays, contact lists, team member grids, and comment threads.
 *
 * This component is part of Trellis's compositive API layer. Prefer the config
 * API, which covers the majority of use cases. Only reach for this component
 * when the config API can't express your pattern, and share your use case in
 * #trellis-support — it directly informs how we evolve the config layer.
 *
 * @see {@link __AvatarConfig | Avatar}
 */
export const Avatar = /*#__PURE__*/forwardRef((t0, ref) => {
  var _ref, _sizeProp;
  const $ = _c(27);
  let children;
  let layoutId;
  let restProps;
  let sizeProp;
  let t1;
  let t2;
  let t3;
  let xstyle;
  if ($[0] !== t0) {
    const {
        backgroundColor: t4,
        children: t5,
        layoutId: t6,
        shape: t7,
        size: t8,
        truncated: t9,
        xstyle: t10
      } = t0,
      t11 = _objectWithoutPropertiesLoose(t0, _excluded3);
    t1 = t4;
    children = t5;
    layoutId = t6;
    t2 = t7;
    sizeProp = t8;
    t3 = t9;
    xstyle = t10;
    restProps = t11;
    $[0] = t0;
    $[1] = children;
    $[2] = layoutId;
    $[3] = restProps;
    $[4] = sizeProp;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = xstyle;
  } else {
    children = $[1];
    layoutId = $[2];
    restProps = $[3];
    sizeProp = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    xstyle = $[8];
  }
  const backgroundColor = t1 === undefined ? "neutral" : t1;
  const shape = t2 === undefined ? "circle" : t2;
  const truncated = t3 === undefined ? false : t3;
  const isInGroup = useContext(AvatarGroupContext);
  const groupSize = useContext(AvatarGroupSizeContext);
  const size = (_ref = (_sizeProp = sizeProp) !== null && _sizeProp !== void 0 ? _sizeProp : groupSize) !== null && _ref !== void 0 ? _ref : "sm";
  let t4;
  if ($[9] !== layoutId) {
    t4 = {
      layoutId
    };
    $[9] = layoutId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    layoutConstraintStyles
  } = useLayoutConstraints(t4);
  let t5;
  if ($[11] !== backgroundColor || $[12] !== isInGroup || $[13] !== layoutConstraintStyles || $[14] !== shape || $[15] !== size || $[16] !== xstyle) {
    t5 = stylex.props(containerBaseStyles.default, containerSizeStyles[size], containerShapeStyles[shape], containerVariantStyles[backgroundColor], isInGroup && containerGroupStyles.default, layoutConstraintStyles, xstyle);
    $[11] = backgroundColor;
    $[12] = isInGroup;
    $[13] = layoutConstraintStyles;
    $[14] = shape;
    $[15] = size;
    $[16] = xstyle;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      [DATA_COMPONENT_ATTRIBUTE]: COMPONENT_NAME
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== children || $[20] !== ref || $[21] !== restProps || $[22] !== t5) {
    t7 = /*#__PURE__*/_jsx(BaseAvatar.Root, Object.assign({}, restProps, {
      ref: ref
    }, t5, t6, {
      children: children
    }));
    $[19] = children;
    $[20] = ref;
    $[21] = restProps;
    $[22] = t5;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const rootElement = t7;
  let t8;
  if ($[24] !== rootElement || $[25] !== truncated) {
    t8 = truncated ? /*#__PURE__*/_jsx(TruncatedAvatarShell, {
      children: rootElement
    }) : rootElement;
    $[24] = rootElement;
    $[25] = truncated;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
});
Avatar.displayName = COMPONENT_NAME;

/**
 * Displays a count (e.g. "+3") inside an avatar-shaped container, used as the
 * last item in a `AvatarGroup` to indicate additional hidden avatars.
 */
export const AvatarGroupCount = /*#__PURE__*/forwardRef((t0, ref) => {
  var _ref2, _sizeProp2;
  const $ = _c(16);
  let children;
  let restProps;
  let sizeProp;
  let t1;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t2,
        shape: t3,
        size: t4,
        xstyle: t5
      } = t0,
      t6 = _objectWithoutPropertiesLoose(t0, _excluded4);
    children = t2;
    t1 = t3;
    sizeProp = t4;
    xstyle = t5;
    restProps = t6;
    $[0] = t0;
    $[1] = children;
    $[2] = restProps;
    $[3] = sizeProp;
    $[4] = t1;
    $[5] = xstyle;
  } else {
    children = $[1];
    restProps = $[2];
    sizeProp = $[3];
    t1 = $[4];
    xstyle = $[5];
  }
  const shape = t1 === undefined ? "circle" : t1;
  const groupSize = useContext(AvatarGroupSizeContext);
  const size = (_ref2 = (_sizeProp2 = sizeProp) !== null && _sizeProp2 !== void 0 ? _sizeProp2 : groupSize) !== null && _ref2 !== void 0 ? _ref2 : "sm";
  let t2;
  if ($[6] !== shape || $[7] !== size || $[8] !== xstyle) {
    t2 = stylex.props(groupCountBaseStyles.default, groupCountSizeStyles[size], containerShapeStyles[shape], xstyle);
    $[6] = shape;
    $[7] = size;
    $[8] = xstyle;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      [DATA_COMPONENT_ATTRIBUTE]: AVATAR_GROUP_COUNT_NAME
    };
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children || $[12] !== ref || $[13] !== restProps || $[14] !== t2) {
    t4 = /*#__PURE__*/_jsx("span", Object.assign({}, restProps, {
      ref: ref
    }, t2, t3, {
      children: children
    }));
    $[11] = children;
    $[12] = ref;
    $[13] = restProps;
    $[14] = t2;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
});
AvatarGroupCount.displayName = AVATAR_GROUP_COUNT_NAME;

/**
 * Groups multiple `Avatar` components with overlapping layout and a shared
 * ring border. Compose with `AvatarGroupCount` as a child to show an
 * overflow indicator (e.g. "+5").
 *
 * This component is part of Trellis's compositive API layer. Prefer the config
 * API, which covers the majority of use cases. Only reach for this component
 * when the config API can't express your pattern, and share your use case in
 * #trellis-support — it directly informs how we evolve the config layer.
 *
 * @see {@link __AvatarGroupConfig | AvatarGroup}
 */
export const AvatarGroup = /*#__PURE__*/forwardRef((t0, ref) => {
  const $ = _c(16);
  let children;
  let restProps;
  let size;
  let xstyle;
  if ($[0] !== t0) {
    const {
        children: t1,
        size: t2,
        xstyle: t3
      } = t0,
      t4 = _objectWithoutPropertiesLoose(t0, _excluded5);
    children = t1;
    size = t2;
    xstyle = t3;
    restProps = t4;
    $[0] = t0;
    $[1] = children;
    $[2] = restProps;
    $[3] = size;
    $[4] = xstyle;
  } else {
    children = $[1];
    restProps = $[2];
    size = $[3];
    xstyle = $[4];
  }
  let t1;
  if ($[5] !== xstyle) {
    t1 = stylex.props(groupBaseStyles.default, xstyle);
    $[5] = xstyle;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [DATA_COMPONENT_ATTRIBUTE]: AVATAR_GROUP_NAME
    };
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== ref || $[10] !== restProps || $[11] !== t1) {
    t3 = /*#__PURE__*/_jsx(AvatarGroupContext.Provider, {
      value: true,
      children: /*#__PURE__*/_jsx("div", Object.assign({}, restProps, {
        ref: ref
      }, t1, t2, {
        children: children
      }))
    });
    $[8] = children;
    $[9] = ref;
    $[10] = restProps;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== size || $[14] !== t3) {
    t4 = /*#__PURE__*/_jsx(AvatarGroupSizeContext.Provider, {
      value: size,
      children: t3
    });
    $[13] = size;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
});
AvatarGroup.displayName = AVATAR_GROUP_NAME;
function _temp() {
  return createTooltipHandle();
}